import React, { useCallback } from 'react'
import { ArtIcon } from '@/atoms/Icons/ArtIcon'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { MoneyIcon } from '@/atoms/Icons/MoneyIcon'
import { UsersThreeIcon } from '@/atoms/Icons/UsersThreeIcon'
import { EyebrowLG, HeadingMD } from '@/atoms/Text'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import useWindowSize from '@/utils/useWindowSize'
import { TorchSubmitButton } from '../ TorchSubmitButton'
import { WhyAngelCard } from '../WhyAngelCard'

export const WhyAngelSection: React.FC = () => {
  const { track } = useSafeAnalytics()
  const { innerWidth } = useWindowSize()
  const { t } = useTranslate('create')

  const handleSubmitClicked = useCallback(() => {
    track('Guild Submit Project Clicked')
  }, [track])

  const iconSize = innerWidth > 1024 ? 64 : innerWidth > 679 ? 48 : 24

  const whyAngelCards = [
    {
      icon: <ArtIcon size={iconSize} color="white" />,
      title: t('creativeControl', 'Creative Control'),
      description: t(
        'creativeControlDescription',
        'Unlike many other studios, you maintain creative control over the content you produce, provided it is approved by the Guild.',
      ),
      wide: false,
    },
    {
      icon: <GuildIconMono size={iconSize} color="white" />,
      title: t('guildFeedback', 'Guild Feedback'),
      description: t(
        'guildFeedbackDescription',
        'After submitting your project, you will receive feedback from the Guild. Angel filmmakers continue to have access to consult with the Guild throughout the creative process.',
      ),
      wide: true,
    },
    {
      icon: <UsersThreeIcon size={iconSize} color="white" />,
      title: t('passionateFanBase', 'Passionate Fan Base'),
      description: t(
        'passionateFanBaseSupportDescription',
        "Our passionate fan base eagerly supports our films and filmmakers. As a Guild-approved filmmaker, you'll reach an enthusiastic audience of millions of people across the globe.",
      ),
      wide: true,
    },
    {
      icon: <MoneyIcon size={iconSize} color="white" />,
      title: t('noDistributionFees', 'No Distribution Fees'),
      description: t(
        'noDistributionFeesIncentivesDescription',
        "We believe in aligning incentives, so that's how we structure our deals with filmmakers. We only make money when you do.",
      ),
      wide: false,
    },
  ]

  return (
    <section
      className="bg-white text-white"
      style={{ backgroundImage: 'linear-gradient(130deg, #232323 0%, #141414 72.76%)' }}
    >
      <div className="h-full w-full max-w-[1440px] px-4 py-16 md:px-12 md:py-[120px] lg:px-[120px] 3lg:mx-auto 3lg:px-0">
        <div className="flex w-full flex-row items-center justify-between">
          <div className="flex flex-col gap-3 se:gap-2">
            <EyebrowLG weight="semibold" color="core-gray-500" className="uppercase">
              {t('theBenefits', 'The Benefits')}
            </EyebrowLG>
            <HeadingMD weight="bold" className="se:photon-title-md md:photon-heading-xl 2xl:photon-display">
              {t('whyAngelStudios', 'Why Angel Studios')}?
            </HeadingMD>
          </div>
          <div className="hidden md:flex">
            <TorchSubmitButton handleSubmitClicked={handleSubmitClicked} />
          </div>
        </div>
        <div className="flex flex-col gap-4 py-10 md:gap-6 md:py-[66px] lg:flex-row lg:flex-wrap lg:py-[100px] se:gap-3 se:py-6">
          {whyAngelCards.map((card) => {
            return <WhyAngelCard key={card.title} {...card} />
          })}
        </div>
        <div className="w-full md:hidden">
          <TorchSubmitButton handleSubmitClicked={handleSubmitClicked} />
        </div>
      </div>
    </section>
  )
}
